// The quick messages at the end of an online game (owner 2026-10-30: "forma podziękowania za gry ... common teksty z
// emotami ... coś się pojawia z podpisem i zaraz znika"): a fixed list, no free text, so nothing to moderate; only the
// message's id goes over the line and each player reads it in their own language. The row sits in the end windows of
// the race and the card games, the bubble pops out of the sender's side or tile, so it needs no name (owner 2026-10-30).
import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import { mpStore, useMultiplayerState, type Said } from '../multiplayer/store';

/** The server's list has the same ids (server/ws/mp2.js SAY); old emoji only, they show on every system. */
const MESSAGES: ReadonlyArray<readonly [string, string]> = [
  ['thanks', '🤝'],
  ['good', '👍'],
  ['congrats', '👏'],
  ['bye', '👋'],
];
/** Between two deals of a card game (server/ws/mp2.js SAY_DEAL; owner 2026-11-02: "niezłe rozdanie", "co za runda"). The
 *  same four in every language: only the id travels. */
const DEAL_MESSAGES: ReadonlyArray<readonly [string, string]> = [
  ['deal-nice', '👍'],
  ['deal-wow', '😮'],
  ['deal-well', '👏'],
  ['deal-lucky', '🍀'], // owner 2026-11-02: not "Powodzenia", which does not fit the middle of a game
];
const emojiOf = (say: string) => [...MESSAGES, ...DEAL_MESSAGES].find(([id]) => id === say)?.[1] ?? '';

/** How long a bubble stays when no new one comes, and how many leaving ones may still fly. */
const SHOW_MS = 4000;
const MAX_LEAVING = 4;
/** As the server's SAY_GAP_MS and SAY_MAX: a tap sooner than this is ignored, the buttons go grey after the last. */
const GAP_MS = 300;
const MAX_SENT = 12;
/** A quick tap keeps its pressed look this long, so it is seen on a touch screen. */
const PRESSED_MS = 180;

/** The buttons. Congratulations only from a player who did not win: from the winner it would read as mockery. Nobody
 *  else at the table when the window opens: no row (alone with bots, the other one left); gone later: grey. `deal`: the
 *  score window between two deals, with the deal's own words (give it the deal as its key: a new count each deal). */
export function QuickSay({ iWon, deal = false }: { iWon: boolean; deal?: boolean }) {
  const mp = useMultiplayerState();
  const present = mp.cards ? (mp.table?.seats.filter(Boolean).length ?? 0) > 1 : !!mp.result && !mp.result.left && !mp.rematch.gone;
  const [worth] = useState(present);
  const [sent, setSent] = useState(0);
  const [pressed, setPressed] = useState<string | null>(null);
  const lastSent = useRef(0);
  const release = useRef(0);
  useEffect(() => () => clearTimeout(release.current), []);
  if (!worth) return null;
  const off = !present || sent >= MAX_SENT;
  const send = (say: string) => {
    clearTimeout(release.current);
    release.current = window.setTimeout(() => setPressed(null), PRESSED_MS);
    const now = Date.now();
    if (now - lastSent.current < GAP_MS) return;
    lastSent.current = now;
    mpStore().say(say);
    setSent(sent + 1);
  };
  return (
    <div className="mp_say_row">
      {(deal ? DEAL_MESSAGES : MESSAGES).filter(([id]) => !(iWon && id === 'congrats')).map(([id, emoji]) => (
        <button
          key={id}
          type="button"
          className="mp_say"
          data-say={id}
          data-pressed={pressed === id || undefined}
          disabled={off}
          onPointerDown={() => setPressed(id)}
          onPointerLeave={() => setPressed((p) => (p === id ? null : p))}
          onClick={() => send(id)}
        >
          <span aria-hidden="true">{emoji}</span> {t(`lobby.say.${id}`)}
        </button>
      ))}
    </div>
  );
}

/** The bubbles of a seat: the last message stays a few seconds; when a new one comes the old one flies up and fades, as
 *  hearts on a stream (owner 2026-10-30: "troszkę instagramowe serduszka"). The leaving ones are gone when their animation ends. */
export function SayBubble({ seat }: { seat: number }) {
  const said = useMultiplayerState().said[seat];
  const n = said?.n ?? null;
  const [bubbles, setBubbles] = useState<Array<Said & { leaving: boolean }>>([]);
  useEffect(() => {
    if (!said) {
      setBubbles([]);
      return undefined;
    }
    const current = said.n;
    setBubbles((list) => [...list.slice(-MAX_LEAVING).map((b) => ({ ...b, leaving: true })), { ...said, leaving: false }]);
    const id = window.setTimeout(() => setBubbles((list) => list.map((b) => (b.n === current ? { ...b, leaving: true } : b))), SHOW_MS);
    return () => clearTimeout(id);
    // a new message is a new n
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  return (
    <>
      {bubbles.map((b) => (
        <div
          key={b.n}
          className="mp_said"
          data-leaving={b.leaving || undefined}
          role={b.leaving ? undefined : 'status'}
          aria-label={`${b.name}: ${t(`lobby.say.${b.say}`)}`}
          onAnimationEnd={() => {
            if (b.leaving) setBubbles((list) => list.filter((x) => x.n !== b.n));
          }}
        >
          <span aria-hidden="true">{emojiOf(b.say)}</span> {t(`lobby.say.${b.say}`)}
        </div>
      ))}
    </>
  );
}
